<template>
  <div class="comparison-page">
    <div class="page-header">
      <h1>方案对比</h1>
      <p class="subtitle">对比不同还款方案的优劣，选择最适合的方案</p>
    </div>
    
    <div v-if="schemes.length === 0" class="empty-state">
      <div class="empty-icon">📊</div>
      <h3>暂无对比方案</h3>
      <p>请先在计算器中添加方案到对比列表</p>
      <router-link to="/" class="btn btn-primary">去计算器</router-link>
    </div>
    
    <div v-else class="comparison-container">
      <!-- 方案列表 -->
      <div class="schemes-list">
        <h2>已保存的方案 ({{ schemes.length }})</h2>
        <div class="schemes-grid">
          <div 
            v-for="(scheme, index) in schemes" 
            :key="scheme.id" 
            class="scheme-card"
            :class="{ active: selectedSchemes.includes(scheme.id) }"
            @click="toggleScheme(scheme.id)"
          >
            <div class="scheme-header">
              <h4>{{ scheme.name }}</h4>
              <div class="scheme-actions">
                <button class="btn-icon" @click.stop="editScheme(scheme)" title="编辑">
                  ✏️
                </button>
                <button class="btn-icon" @click.stop="removeScheme(index)" title="删除">
                  🗑️
                </button>
              </div>
            </div>
            <div class="scheme-info">
              <div class="info-item">
                <span class="label">贷款金额</span>
                <span class="value">{{ formatCurrency(scheme.loanAmount) }}</span>
              </div>
              <div class="info-item">
                <span class="label">贷款期限</span>
                <span class="value">{{ scheme.loanTerm }}期</span>
              </div>
              <div class="info-item">
                <span class="label">贷款利率</span>
                <span class="value">{{ formatPercentage(scheme.interestRate) }}%</span>
              </div>
              <div class="info-item">
                <span class="label">贷款方式</span>
                <span class="value">{{ getRepaymentMethodName(scheme.repaymentMethod) }}</span>
              </div>
            </div>
            <div class="scheme-results" v-if="scheme.results">
              <div class="result-item" v-if="scheme.results.formData.prepaymentMethod !== 'no_prepayment'">
                <span class="label">提前还款</span>
                <span class="value">{{ formatCurrency(scheme.results.formData.prepaymentAmount || 0) }}</span>
              </div>
              <div class="result-item" v-if="scheme.results.formData.prepaymentMethod !== 'no_prepayment'">
                <span class="label">剩余本金</span>
                <span class="value">{{ formatCurrency(scheme.results.prepaymentPlan.loanAmount || 0) }}</span>
              </div>
              <div class="result-item" v-if="scheme.results.formData.prepaymentMethod !== 'no_prepayment'">
                <span class="label">还款利率</span>
                <span class="value">{{ formatPercentage(scheme.results.prepaymentPlan.interestRate || scheme.results.originalPlan.interestRate) }}%</span>
              </div>
              <div class="result-item">
                <span class="label">首月还款</span>
                <span class="value">{{ formatCurrency(scheme.results.prepaymentPlan.monthlyPayment || scheme.results.originalPlan.monthlyPayment) }}</span>
              </div>
              <div class="result-item">
                <span class="label">剩余利息</span>
                <span class="value">{{ formatCurrency(scheme.results.prepaymentPlan.totalInterest || scheme.results.originalPlan.totalInterest) }}</span>
              </div>
              <div class="result-item">
                <span class="label">剩余还款</span>
                <span class="value">{{ formatCurrency(scheme.results.prepaymentPlan.totalPayment || scheme.results.originalPlan.totalPayment) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 对比表格 -->
      <div v-if="selectedSchemes.length > 0" class="comparison-table-section">
        <h2>方案对比表</h2>
        <div class="table-controls">
          <div class="control-group">
            <label>对比维度：</label>
            <select v-model="comparisonDimension">
              <option value="original">原还款计划</option>
              <option value="prepayment">提前还款后</option>
              <option value="savings">节省情况</option>
            </select>
          </div>
          <div class="control-group">
            <label>排序方式：</label>
            <select v-model="sortBy" @change="sortSchemes">
              <option value="monthlyPayment">月供</option>
              <option value="totalInterest">总利息</option>
              <option value="totalPayment">总还款</option>
              <option value="savingPercentage">节省比例</option>
            </select>
          </div>
        </div>
        
        <div class="comparison-table-container">
          <table class="comparison-table">
            <thead>
              <tr>
                <th>方案名称</th>
                <th>贷款金额</th>
                <th>贷款期限</th>
                <th>贷款利率</th>
                <th>贷款方式</th>
                <th>首月还款</th>
                <th>剩余利息</th>
                <th>剩余还款</th>
                <th v-if="comparisonDimension === 'savings'">节省利息</th>
                <th v-if="comparisonDimension === 'savings'">节省比例</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="scheme in sortedSchemes" :key="scheme.id">
                <td class="scheme-name">{{ scheme.name }}</td>
                <td>{{ formatCurrency(scheme.loanAmount) }}</td>
                <td>{{ scheme.loanTerm }}期</td>
                <td>{{ formatPercentage(scheme.interestRate) }}%</td>
                <td>{{ getRepaymentMethodName(scheme.repaymentMethod) }}</td>
                <td class="amount">{{ formatCurrency(getComparisonValue(scheme, 'monthlyPayment')) }}</td>
                <td class="amount">{{ formatCurrency(getComparisonValue(scheme, 'totalInterest')) }}</td>
                <td class="amount">{{ formatCurrency(getComparisonValue(scheme, 'totalPayment')) }}</td>
                <td v-if="comparisonDimension === 'savings'" class="amount save">
                  {{ formatCurrency(scheme.results?.comparison?.interestSaved || 0) }}
                </td>
                <td v-if="comparisonDimension === 'savings'" class="percentage save">
                  {{ scheme.results?.comparison?.savingPercentage || 0 }}%
                </td>
                <td class="actions">
                  <button class="btn btn-sm" @click="viewDetails(scheme)">详情</button>
                  <button class="btn btn-sm btn-danger" @click="removeScheme(schemes.findIndex(s => s.id === scheme.id))">删除</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      
      <!-- 图表对比 -->
      <div v-if="selectedSchemes.length > 1" class="chart-section">
        <h2>可视化对比</h2>
        <div class="chart-container">
          <div class="chart-item">
            <h3>月供对比</h3>
            <div class="bar-chart">
              <div 
                v-for="scheme in selectedSchemesData" 
                :key="scheme.id"
                class="bar-item"
              >
                <div class="bar-label">{{ scheme.name }}</div>
                <div class="bar-container">
                  <div 
                    class="bar" 
                    :style="{ width: getBarWidth(scheme, 'monthlyPayment') + '%' }"
                  ></div>
                </div>
                <div class="bar-value">{{ formatCurrency(getComparisonValue(scheme, 'monthlyPayment')) }}</div>
              </div>
            </div>
          </div>
          
          <div class="chart-item">
            <h3>总利息对比</h3>
            <div class="bar-chart">
              <div 
                v-for="scheme in selectedSchemesData" 
                :key="scheme.id"
                class="bar-item"
              >
                <div class="bar-label">{{ scheme.name }}</div>
                <div class="bar-container">
                  <div 
                    class="bar" 
                    :style="{ width: getBarWidth(scheme, 'totalInterest') + '%' }"
                  ></div>
                </div>
                <div class="bar-value">{{ formatCurrency(getComparisonValue(scheme, 'totalInterest')) }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mortgageCalculator } from '../utils/calculator'

export default {
  name: 'Comparison',
  data() {
    return {
      schemes: [],
      selectedSchemes: [],
      comparisonDimension: 'prepayment',
      sortBy: 'monthlyPayment'
    }
  },
  computed: {
    selectedSchemesData() {
      return this.schemes.filter(scheme => this.selectedSchemes.includes(scheme.id))
    },
    sortedSchemes() {
      const schemes = [...this.selectedSchemesData]
      return schemes.sort((a, b) => {
        const aValue = this.getComparisonValue(a, this.sortBy)
        const bValue = this.getComparisonValue(b, this.sortBy)
        return bValue - aValue
      })
    }
  },
  mounted() {
    this.loadSchemes()
  },
  methods: {
    loadSchemes() {
      const saved = localStorage.getItem('mortgageSchemes')
      if (saved) {
        this.schemes = JSON.parse(saved)
      }
    },
    
    toggleScheme(schemeId) {
      const index = this.selectedSchemes.indexOf(schemeId)
      if (index > -1) {
        this.selectedSchemes.splice(index, 1)
      } else {
        this.selectedSchemes.push(schemeId)
      }
    },
    
    editScheme(scheme) {
      // 跳转到计算器页面并加载方案数据
      this.$router.push({
        name: 'Calculator',
        query: { edit: scheme.id }
      })
    },
    
    removeScheme(index) {
      if (confirm('确定要删除这个方案吗？')) {
        const scheme = this.schemes[index]
        this.schemes.splice(index, 1)
        this.selectedSchemes = this.selectedSchemes.filter(id => id !== scheme.id)
        this.saveSchemes()
      }
    },
    
    viewDetails(scheme) {
      if (scheme.results) {
        this.$router.push({
          name: 'Results',
          params: { results: scheme.results }
        })
      }
    },
    
    getComparisonValue(scheme, field) {
      if (!scheme.results) return 0
      
      if (this.comparisonDimension === 'original') {
        return scheme.results.originalPlan[field] || 0
      } else if (this.comparisonDimension === 'prepayment') {
        return scheme.results.prepaymentPlan[field] || 0
      } else if (this.comparisonDimension === 'savings') {
        if (field === 'monthlyPayment') {
          return scheme.results.originalPlan.monthlyPayment - scheme.results.prepaymentPlan.monthlyPayment
        } else if (field === 'totalInterest') {
          return scheme.results.comparison.interestSaved
        } else if (field === 'totalPayment') {
          return scheme.results.comparison.totalSaved
        }
      }
      
      // 默认显示提前还款后的数据
      return scheme.results.prepaymentPlan[field] || scheme.results.originalPlan[field] || 0
    },
    
    getBarWidth(scheme, field) {
      const values = this.selectedSchemesData.map(s => this.getComparisonValue(s, field))
      const maxValue = Math.max(...values)
      const currentValue = this.getComparisonValue(scheme, field)
      return maxValue > 0 ? (currentValue / maxValue * 100) : 0
    },
    
    getRepaymentMethodName(method) {
      return method === 'equal_payment' ? '等额本息' : '等额本金'
    },
    
    formatCurrency(amount) {
      if (isNaN(amount) || amount === null || amount === undefined) {
        return '0.00'
      }
      return mortgageCalculator.formatCurrency(amount)
    },
    
    formatPercentage(rate) {
      if (isNaN(rate) || rate === null || rate === undefined) {
        return '0.00'
      }
      return parseFloat(rate).toFixed(2)
    },
    
    sortSchemes() {
      // 排序逻辑在computed中处理
    },
    
    saveSchemes() {
      localStorage.setItem('mortgageSchemes', JSON.stringify(this.schemes))
    }
  }
}
</script>

<style scoped>
.comparison-page {
  max-width: 1200px;
  margin: 0 auto;
  min-height: 100%;
  display: flex;
  flex-direction: column;
}

.page-header {
  text-align: center;
  margin-bottom: 30px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 30px;
  border-radius: 10px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
}

.page-header h1 {
  font-size: 2.5em;
  margin-bottom: 10px;
  font-weight: 300;
}

.subtitle {
  font-size: 1.1em;
  opacity: 0.9;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.empty-icon {
  font-size: 4em;
  margin-bottom: 20px;
}

.empty-state h3 {
  color: #333;
  margin-bottom: 10px;
  font-size: 1.5em;
}

.empty-state p {
  color: #666;
  margin-bottom: 30px;
  font-size: 1.1em;
}

.btn {
  padding: 12px 24px;
  border: none;
  border-radius: 5px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s;
  font-weight: 500;
  text-decoration: none;
  display: inline-block;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
}

.schemes-list {
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  padding: 25px;
  margin-bottom: 30px;
}

.schemes-list h2 {
  color: #333;
  margin-bottom: 20px;
  font-size: 1.5em;
}

.schemes-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}

.scheme-card {
  background: #f8f9fa;
  border: 2px solid transparent;
  border-radius: 10px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s;
}

.scheme-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
}

.scheme-card.active {
  border-color: #667eea;
  background: #f0f4ff;
}

.scheme-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.scheme-header h4 {
  color: #333;
  margin: 0;
  font-size: 1.2em;
}

.scheme-actions {
  display: flex;
  gap: 8px;
}

.btn-icon {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.3s;
}

.btn-icon:hover {
  background: rgba(0,0,0,0.1);
}

.scheme-info {
  margin-bottom: 15px;
}

.info-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}

.info-item .label {
  color: #666;
  font-size: 0.9em;
}

.info-item .value {
  color: #333;
  font-weight: 600;
}

.scheme-results {
  border-top: 1px solid #e9ecef;
  padding-top: 15px;
}

.result-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;
}

.result-item .label {
  color: #666;
  font-size: 0.9em;
}

.result-item .value {
  color: #333;
  font-weight: 600;
  font-size: 1.1em;
}

.comparison-table-section {
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  padding: 25px;
  margin-bottom: 30px;
}

.comparison-table-section h2 {
  color: #333;
  margin-bottom: 20px;
  font-size: 1.5em;
}

.table-controls {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.control-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

.control-group label {
  font-weight: 500;
  color: #555;
}

.control-group select {
  padding: 8px 12px;
  border: 2px solid #e1e5e9;
  border-radius: 5px;
  font-size: 14px;
}

.comparison-table-container {
  overflow-x: auto;
}

.comparison-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.comparison-table th,
.comparison-table td {
  padding: 12px;
  text-align: center;
  border-bottom: 1px solid #e9ecef;
  vertical-align: middle;
}

.comparison-table th {
  background: #667eea;
  color: white;
  font-weight: 600;
  position: sticky;
  top: 0;
}

.comparison-table tr:hover {
  background: #f8f9fa;
}

.comparison-table .scheme-name {
  text-align: left;
  font-weight: 600;
  color: #333;
}

.comparison-table .amount {
  text-align: center;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
  font-weight: 500;
  letter-spacing: 0.3px;
}

.comparison-table .percentage {
  text-align: center;
}

.comparison-table .save {
  color: #28a745;
  font-weight: 600;
}

.comparison-table .actions {
  white-space: nowrap;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 14px;
  margin: 0 2px;
}

.btn-danger {
  background: #dc3545;
  color: white;
}

.btn-danger:hover {
  background: #c82333;
}

.chart-section {
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  padding: 25px;
}

.chart-section h2 {
  color: #333;
  margin-bottom: 20px;
  font-size: 1.5em;
}

.chart-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 30px;
}

.chart-item h3 {
  color: #333;
  margin-bottom: 15px;
  font-size: 1.2em;
}

.bar-chart {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.bar-item {
  display: flex;
  align-items: center;
  gap: 15px;
}

.bar-label {
  min-width: 120px;
  font-size: 0.9em;
  color: #666;
  text-align: right;
}

.bar-container {
  flex: 1;
  height: 25px;
  background: #e9ecef;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
}

.bar {
  height: 100%;
  background: linear-gradient(90deg, #667eea, #764ba2);
  border-radius: 12px;
  transition: width 0.5s ease;
}

.bar-value {
  min-width: 100px;
  text-align: right;
  font-size: 0.9em;
  color: #666;
  font-family: 'Courier New', monospace;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .comparison-page {
    padding: 10px;
  }
  
  .page-header {
    padding: 20px 15px;
    margin-bottom: 20px;
  }
  
  .page-header h1 {
    font-size: 1.8em;
    margin-bottom: 8px;
  }
  
  .subtitle {
    font-size: 1em;
  }
  
  .empty-state {
    padding: 40px 15px;
  }
  
  .empty-icon {
    font-size: 3em;
    margin-bottom: 15px;
  }
  
  .empty-state h3 {
    font-size: 1.3em;
    margin-bottom: 8px;
  }
  
  .empty-state p {
    font-size: 1em;
    margin-bottom: 25px;
  }
  
  .btn {
    width: 100%;
    padding: 12px;
    font-size: 14px;
  }
  
  .schemes-list {
    padding: 15px;
    margin-bottom: 20px;
  }
  
  .schemes-list h2 {
    font-size: 1.3em;
    margin-bottom: 15px;
  }
  
  .schemes-grid {
    grid-template-columns: 1fr;
    gap: 15px;
  }
  
  .scheme-card {
    padding: 15px;
  }
  
  .scheme-header h4 {
    font-size: 1.1em;
  }
  
  .scheme-actions {
    gap: 6px;
  }
  
  .btn-icon {
    font-size: 14px;
    padding: 6px;
  }
  
  .info-item {
    margin-bottom: 6px;
  }
  
  .info-item .label {
    font-size: 13px;
  }
  
  .info-item .value {
    font-size: 14px;
  }
  
  .result-item {
    margin-bottom: 4px;
  }
  
  .result-item .label {
    font-size: 13px;
  }
  
  .result-item .value {
    font-size: 14px;
  }
  
  .comparison-table-section {
    padding: 15px;
    margin-bottom: 20px;
  }
  
  .comparison-table-section h2 {
    font-size: 1.3em;
    margin-bottom: 15px;
  }
  
  .table-controls {
    flex-direction: column;
    gap: 10px;
    margin-bottom: 15px;
  }
  
  .control-group {
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
  }
  
  .control-group label {
    font-size: 14px;
  }
  
  .control-group select {
    width: 100%;
    padding: 8px;
    font-size: 14px;
  }
  
  .comparison-table-container {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  
  .comparison-table {
    font-size: 0.8em;
    min-width: 700px;
  }
  
  .comparison-table th,
  .comparison-table td {
    padding: 6px 4px;
    white-space: nowrap;
  }
  
  .comparison-table .scheme-name {
    max-width: 100px;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .comparison-table .amount {
    font-size: 0.9em;
  }
  
  .comparison-table .actions {
    white-space: nowrap;
  }
  
  .btn-sm {
    padding: 4px 8px;
    font-size: 12px;
    margin: 0 1px;
  }
  
  .chart-section {
    padding: 15px;
  }
  
  .chart-section h2 {
    font-size: 1.3em;
    margin-bottom: 15px;
  }
  
  .chart-container {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  .chart-item h3 {
    font-size: 1.1em;
    margin-bottom: 12px;
  }
  
  .bar-chart {
    gap: 12px;
  }
  
  .bar-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  
  .bar-label {
    min-width: auto;
    text-align: left;
    font-size: 13px;
  }
  
  .bar-container {
    width: 100%;
    height: 20px;
  }
  
  .bar-value {
    min-width: auto;
    font-size: 12px;
  }
}

/* 超小屏幕适配 */
@media (max-width: 480px) {
  .comparison-page {
    padding: 5px;
  }
  
  .page-header {
    padding: 15px 10px;
  }
  
  .page-header h1 {
    font-size: 1.5em;
  }
  
  .subtitle {
    font-size: 0.9em;
  }
  
  .empty-state {
    padding: 30px 10px;
  }
  
  .empty-icon {
    font-size: 2.5em;
  }
  
  .empty-state h3 {
    font-size: 1.2em;
  }
  
  .empty-state p {
    font-size: 0.9em;
  }
  
  .schemes-list {
    padding: 12px;
  }
  
  .scheme-card {
    padding: 12px;
  }
  
  .scheme-header h4 {
    font-size: 1em;
  }
  
  .comparison-table-section {
    padding: 12px;
  }
  
  .comparison-table {
    font-size: 0.75em;
    min-width: 600px;
  }
  
  .comparison-table th,
  .comparison-table td {
    padding: 4px 2px;
  }
  
  .chart-section {
    padding: 12px;
  }
  
  .chart-item h3 {
    font-size: 1em;
  }
  
  .bar-chart {
    gap: 10px;
  }
  
  .bar-label {
    font-size: 12px;
  }
  
  .bar-container {
    height: 18px;
  }
  
  .bar-value {
    font-size: 11px;
  }
}
</style>
